import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { enablePushNotifications } from '@/lib/pushNotifications';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const [staffPortalMode, setStaffPortalModeState] = useState(() =>
    localStorage.getItem('staff_portal_mode') === 'professor'
      ? 'professor'
      : 'aluno'
  );

  const checkAuth = useCallback(async () => {
    const stored = localStorage.getItem('access_token');
    if (!stored) {
      setLoading(false);
      return;
    }
    try {
      const res = await api.get('/auth/me');
      const userData = res.data;

      setUser(userData);

      if (
        userData?.role !== 'aluno' ||
        userData?.acesso_staff !== true
      ) {
        localStorage.removeItem('staff_portal_mode');
        setStaffPortalModeState('aluno');
      }
    } catch {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      setUser(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    const { access_token, refresh_token, user: userData } = res.data;
    localStorage.setItem('access_token', access_token);
    localStorage.setItem('refresh_token', refresh_token);
    setUser(userData);

    localStorage.removeItem('staff_portal_mode');
    setStaffPortalModeState('aluno');

    enablePushNotifications().catch((err) => console.log('Push notification error:', err));
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('staff_portal_mode');
    setStaffPortalModeState('aluno');
    setUser(null);
  };

  const setStaffPortalMode = (mode) => {
    const podeUsar =
      user?.role === 'aluno' &&
      user?.acesso_staff === true;

    const nextMode =
      podeUsar && mode === 'professor'
        ? 'professor'
        : 'aluno';

    localStorage.setItem('staff_portal_mode', nextMode);
    setStaffPortalModeState(nextMode);
  };

  const effectiveRole =
    user?.role === 'aluno' &&
    user?.acesso_staff === true &&
    staffPortalMode === 'professor'
      ? 'professor_staff'
      : user?.role;

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        logout,
        setUser,
        staffPortalMode,
        setStaffPortalMode,
        effectiveRole
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
